import React from 'react';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { FiInfo } from 'react-icons/fi';

import { formatDate, formatWindowRange } from './clientCard.format';
import { CARD_WINDOW_DAYS_BACK, CARD_WINDOW_DAYS_FORWARD, type CardWindow } from './clientCard.model';

type CardWindowNoteProps = {
  /** The count this panel is showing, already worded and pluralised. */
  summary: string;
  window: CardWindow;
  /** A sentence this panel wants said first, before the window is explained. */
  lead?: string;
};

/**
 * What a panel's count was counted over.
 *
 * The span used to sit beside the count as a bare "over 19/05 – 15/11/2026", which
 * says when without saying what: readers took the dates for the client's history
 * rather than for the limit of what was fetched. So the count and the span are one
 * phrase now — "15 visits in 19/05 – 15/11/2026" — and the icon carries the part
 * that will not fit in a header, namely that this is a window and that anything
 * outside it is absent rather than non-existent.
 */
const CardWindowNote: React.FC<CardWindowNoteProps> = ({ summary, window, lead }) => {
  const { t, i18n } = useTranslation();

  const explanation = [
    lead,
    t('client-card-window-explained', {
      from: formatDate(window.from, i18n.language),
      to: formatDate(window.to, i18n.language),
      back: CARD_WINDOW_DAYS_BACK,
      forward: CARD_WINDOW_DAYS_FORWARD,
    }),
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className="flex flex-wrap items-center justify-end gap-x-1 text-[12px] text-slate-400">
      <span>
        {t('client-card-window-counted', {
          summary,
          range: formatWindowRange(window.from, window.to, i18n.language),
        })}
      </span>
      <Tooltip title={explanation}>
        {/* A button, not a bare icon: the explanation has to be reachable without a
            pointer, and a focusable element is the only way a tooltip is. */}
        <button
          type="button"
          aria-label={explanation}
          className="flex cursor-help items-center border-0 bg-transparent p-0 text-slate-300 transition-colors hover:text-slate-500"
        >
          <FiInfo aria-hidden="true" className="text-[13px]" />
        </button>
      </Tooltip>
    </span>
  );
};

export default CardWindowNote;
